/**
 * Illustrations for the Typesetter page, drawn in HTML and CSS in the app's
 * palette rather than taken as screenshots. Each vignette is a single
 * `role="img"` with an alt-style label; the markup inside is decorative.
 * The two example books use real covers fetched once from Open Library by
 * scripts/typesetter-images.py.
 */
import React, { useEffect, useState } from 'react';

import emmaCover from '../images/typesetter/emma-cover.jpg';
import lessonsCover from '../images/typesetter/lessons-of-history-cover.jpg';

// Every cover file the page shows, for the home page to warm before click-through.
export const COVERS = [emmaCover, lessonsCover];

/* ---------- Primitives ---------- */

const BOOKS = {
  emma: {
    title: 'Emma',
    author: 'Jane Austen',
    year: 1815,
    pages: 474,
    page: 286,
    cover: emmaCover,
    coverWidth: 300,
    coverHeight: 466,
  },
  lessons: {
    title: 'The Lessons of History',
    author: 'Will and Ariel Durant',
    year: 1968,
    pages: 117,
    page: 44,
    cover: lessonsCover,
    coverWidth: 300,
    coverHeight: 433,
  },
};

// Spines on the Bookshelf shelf: cloth tones only, no cover art needed.
const SPINES = [
  { title: 'Walden', tone: 'green' },
  { title: 'The Odyssey', tone: 'ochre' },
  { title: 'Jane Eyre', tone: 'plum' },
  { title: 'Moby-Dick', tone: 'teal' },
  { title: 'North and South', tone: 'brown' },
];

// Eager, not lazy: the covers are small (30-60 KB) and the same two files are
// reused down the page, and lazy images in the first viewport paint late in
// Safari. scripts/typesetter-postbuild.js also preloads them from the HTML.
export function Cover({ book, size = 'md' }) {
  return (
    <span className={`ts-cover ts-cover--${size}`}>
      <img
        src={book.cover}
        alt=""
        width={book.coverWidth}
        height={book.coverHeight}
        decoding="async"
      />
    </span>
  );
}

function Bar({ value, tone = 'green', thin = false }) {
  return (
    <span className={`ts-bar${thin ? ' ts-bar--thin' : ''} ts-bar--${tone}`}>
      <span style={{ width: `${value}%` }} />
    </span>
  );
}

function Flame({ size = 14 }) {
  return (
    <svg className="ts-flame" width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 1.5c.6 2.2 2.6 3.2 3.6 5.1 1.2 2.4.3 5.6-2.3 7.1-.5.3-1 .4-1.3.4-2.9 0-4.9-2.5-4.4-5.2.3-1.6 1.4-2.4 1.9-3.4.2.9.7 1.6 1.3 1.9-.2-2.2.2-4.3 1.2-5.9z"
        fill="currentColor"
      />
    </svg>
  );
}

function Ring({ value, size = 64, stroke = 7 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg className="ts-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#c8e6c9" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="#4a7c4a"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - value)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

function Icon({ name }) {
  const paths = {
    shelf: 'M3 3h3v14H3zM8 3h3v14H8zM13.2 3.6l2.9-.8 3.6 13.5-2.9.8z',
    book: 'M3 4.5C5.5 3.3 8 3.5 10 5v12c-2-1.5-4.5-1.7-7-.5zM17 4.5C14.5 3.3 12 3.5 10 5v12c2-1.5 4.5-1.7 7-.5z',
    note: 'M5 3h10v14H5zM7.5 7h5M7.5 10h5M7.5 13h3',
    chart: 'M4 16V9M9 16V4M14 16v-5M2 17h16',
    camera: 'M3 6.5h3.2l1.3-2h5l1.3 2H17v9.5H3zM10 8.5a3 3 0 110 6 3 3 0 010-6z',
    folder: 'M2.5 5.5h5l1.5 1.5h8.5v9h-15z',
    doc: 'M5 2.5h6.5l4 4v11H5zM11.5 2.5v4h4',
    chevron: 'M7 4l6 6-6 6',
    scan: 'M3 7V3h4M13 3h4v4M17 13v4h-4M7 17H3v-4',
    play: 'M6 4l10 6-10 6z',
    check: 'M4 10.5l4 4 8-9',
    cloud: 'M6 15.5a3.5 3.5 0 01-.5-7 5 5 0 019.7 1A3 3 0 0115 15.5z',
    bold: 'M6 3.5h5a3 3 0 010 6H6zM6 9.5h5.8a3.2 3.2 0 010 6.5H6z',
    italic: 'M8 3.5h7M5 16.5h7M12 3.5l-4 13',
    list: 'M7 5h10M7 10h10M7 15h10M3.5 5h.01M3.5 10h.01M3.5 15h.01',
  };
  return (
    <svg className="ts-icon" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d={paths[name]}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StatusBar() {
  return (
    <div className="ts-status">
      <span>9:41</span>
      <span className="ts-status-right">
        <svg width="16" height="10" viewBox="0 0 16 10" aria-hidden="true">
          <rect x="0" y="6" width="3" height="4" rx="0.8" fill="currentColor" />
          <rect x="4.3" y="4" width="3" height="6" rx="0.8" fill="currentColor" />
          <rect x="8.6" y="2" width="3" height="8" rx="0.8" fill="currentColor" />
          <rect x="12.9" y="0" width="3" height="10" rx="0.8" fill="currentColor" />
        </svg>
        <svg width="24" height="11" viewBox="0 0 24 11" aria-hidden="true">
          <rect x="0.5" y="0.5" width="20" height="10" rx="3" fill="none" stroke="currentColor" />
          <rect x="2" y="2" width="15" height="7" rx="1.6" fill="currentColor" />
          <rect x="22" y="3.5" width="1.5" height="4" rx="0.7" fill="currentColor" />
        </svg>
      </span>
    </div>
  );
}

function TabBar({ active }) {
  const tabs = [
    ['shelf', 'Bookshelf'],
    ['book', 'Reading'],
    ['note', 'Notes'],
    ['chart', 'Stats'],
  ];
  return (
    <div className="ts-tabbar">
      {tabs.map(([icon, label]) => (
        <span key={label} className={`ts-tab${label === active ? ' is-active' : ''}`}>
          <Icon name={icon} />
          <span>{label}</span>
        </span>
      ))}
    </div>
  );
}

function BookCard({ book, timer = false }) {
  const pct = Math.round((book.page / book.pages) * 100);
  return (
    <div className="ts-bookcard">
      <Cover book={book} size="sm" />
      <div className="ts-bookcard-body">
        <strong>{book.title}</strong>
        <span className="ts-soft">{book.author}</span>
        <Bar value={pct} />
        <span className="ts-caption">
          p. {book.page} of {book.pages}
        </span>
        {timer && (
          <span className="ts-pill ts-pill--timer">
            <Icon name="play" /> Start reading
          </span>
        )}
      </div>
    </div>
  );
}

function GoalCard() {
  return (
    <div className="ts-goalcard">
      <div className="ts-goalcard-row">
        <span className="ts-caption">This week</span>
        <span className="ts-streak">
          <Flame /> 12 days
        </span>
      </div>
      <div className="ts-goalcard-num">
        <strong>3h 20m</strong>
        <span className="ts-soft">of 5h</span>
      </div>
      <Bar value={66} />
    </div>
  );
}

/* ---------- Hero: the Reading tab in a phone ---------- */

export function HeroPhone({ alt }) {
  return (
    <div className="ts-phone" role="img" aria-label={alt}>
      <div className="ts-screen" aria-hidden="true">
        <StatusBar />
        <h2 className="ts-screen-title">Reading</h2>
        <GoalCard />
        <BookCard book={BOOKS.emma} timer />
        <BookCard book={BOOKS.lessons} />
        <TabBar active="Reading" />
      </div>
    </div>
  );
}

/* ---------- Shelves ---------- */

function Spines({ books }) {
  return (
    <div className="ts-spines">
      {books.map((book, i) => (
        <span
          key={book.title}
          className={`ts-spine ts-cover--${book.tone}`}
          style={{ height: `${72 + ((i * 7) % 20)}%` }}
        >
          <span>{book.title}</span>
        </span>
      ))}
      <span className="ts-plank" />
    </div>
  );
}

export function ShelfVignette({ kind }) {
  if (kind === 'Bookshelf') {
    return (
      <div className="ts-shelf-vig" aria-hidden="true">
        <Spines books={SPINES} />
      </div>
    );
  }
  if (kind === 'Currently Reading') {
    return (
      <div className="ts-shelf-vig" aria-hidden="true">
        <BookCard book={BOOKS.emma} />
      </div>
    );
  }
  return (
    <div className="ts-shelf-vig" aria-hidden="true">
      <div className="ts-bookcard ts-bookcard--done">
        <Cover book={BOOKS.lessons} size="sm" />
        <div className="ts-bookcard-body">
          <strong>{BOOKS.lessons.title}</strong>
          <span className="ts-soft">{BOOKS.lessons.author}</span>
          <span className="ts-chips">
            <span className="ts-chip ts-chip--green">
              <Icon name="check" /> Finished 2 Sep
            </span>
            <span className="ts-chip">9 notes</span>
            <span className="ts-chip">3h 05m</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export function BarcodeVignette({ alt }) {
  const bars = [3, 1, 2, 1, 1, 3, 2, 1, 1, 2, 3, 1, 2, 2, 1, 3, 1, 1, 2, 1, 3, 2, 1, 1, 2, 1, 3];
  return (
    <div className="ts-scan" role="img" aria-label={alt}>
      <div className="ts-scan-code" aria-hidden="true">
        <span className="ts-scan-corner" />
        <span className="ts-barcode">
          {bars.map((w, i) => (
            <span key={i} style={{ width: `${w * 1.6}px` }} />
          ))}
        </span>
        <span className="ts-caption">978 0 14 143958 7</span>
      </div>
      <div className="ts-scan-arrow" aria-hidden="true">
        <Icon name="chevron" />
      </div>
      <dl className="ts-scan-form" aria-hidden="true">
        <div>
          <dt>Title</dt>
          <dd>{BOOKS.emma.title}</dd>
        </div>
        <div>
          <dt>Author</dt>
          <dd>{BOOKS.emma.author}</dd>
        </div>
        <div>
          <dt>Year</dt>
          <dd>{BOOKS.emma.year}</dd>
        </div>
        <div>
          <dt>Pages</dt>
          <dd>{BOOKS.emma.pages}</dd>
        </div>
      </dl>
    </div>
  );
}

/* ---------- Timer ---------- */

/** Counts up once a second from `start` seconds, unless motion is reduced. */
export function useTicker(start) {
  const [seconds, setSeconds] = useState(start);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const id = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function LockScreen({ alt, elapsed }) {
  const book = BOOKS.emma;
  return (
    <div className="ts-lock" role="img" aria-label={alt}>
      <div className="ts-lock-inner" aria-hidden="true">
        <span className="ts-island">
          <Cover book={book} size="xs" />
          <span className="ts-island-time">{elapsed}</span>
        </span>
        <span className="ts-lock-date">Sunday 13 September</span>
        <span className="ts-lock-time">9:41</span>
        <div className="ts-activity">
          <Cover book={book} size="sm" />
          <div className="ts-activity-body">
            <strong>{book.title}</strong>
            <span className="ts-soft">Reading · p. {book.page}</span>
          </div>
          <span className="ts-activity-time">{elapsed}</span>
        </div>
      </div>
    </div>
  );
}

export function NotesHeader({ alt, elapsed }) {
  const book = BOOKS.emma;
  return (
    <div className="ts-noteshead" role="img" aria-label={alt}>
      <div aria-hidden="true">
        <div className="ts-navbar">
          <span className="ts-navbar-back">
            <Icon name="chevron" /> Reading
          </span>
          <span className="ts-navbar-action">Share</span>
        </div>
        <div className="ts-noteshead-book">
          <Cover book={book} size="md" />
          <div>
            <h3 className="ts-screen-title ts-screen-title--sm">{book.title}</h3>
            <span className="ts-soft">
              {book.author} · {book.year}
            </span>
            <Bar value={60} />
            <span className="ts-caption">
              p. {book.page} of {book.pages}
            </span>
          </div>
        </div>
        <div className="ts-noteshead-timer">
          <span className="ts-pill ts-pill--timer is-running">
            <span className="ts-dot" /> Reading {elapsed}
          </span>
          <span className="ts-caption">Total 11h 40m across 9 sessions</span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Notes editor ---------- */

export function NotesEditor({ alt }) {
  return (
    <div className="ts-editor" role="img" aria-label={alt}>
      <div aria-hidden="true">
        <div className="ts-toolbar">
          <span className="ts-toolbar-btn">H</span>
          <span className="ts-toolbar-btn">
            <Icon name="bold" />
          </span>
          <span className="ts-toolbar-btn">
            <Icon name="italic" />
          </span>
          <span className="ts-toolbar-btn">
            <Icon name="list" />
          </span>
          <span className="ts-toolbar-btn ts-toolbar-btn--camera">
            <Icon name="camera" />
          </span>
        </div>
        <div className="ts-editor-page">
          <h4>Volume 1, Chapter 8</h4>
          <p>
            Emma talks Harriet out of Robert Martin, and Knightley is the only one in the room who
            sees what she has done.
          </p>
          <ul>
            <li>Mr Woodhouse and the gruel</li>
            <li>
              Every match Emma makes is really about <em>Emma</em>
            </li>
            <li>Come back to this after Box Hill</li>
          </ul>
          <blockquote>
            <span className="ts-chip ts-chip--green">
              <Icon name="camera" /> From the camera
            </span>
            “Seldom, very seldom, does complete truth belong to any human disclosure; seldom can it
            happen that something is not a little disguised, or a little mistaken.”
            <span className="ts-caption">p. 391</span>
          </blockquote>
        </div>
      </div>
    </div>
  );
}

/* ---------- Stats and widgets ---------- */

const WEEKS = [2.1, 3.4, 4.8, 5.6, 3.0, 4.2, 5.1, 5.3, 2.4, 4.9, 5.7, 3.3];

export function StatsCard({ alt }) {
  const goal = 5;
  const max = 6;
  const w = 264;
  const h = 96;
  const gap = 8;
  const bw = (w - gap * (WEEKS.length - 1)) / WEEKS.length;
  const goalY = h - (goal / max) * h;
  return (
    <div className="ts-stats" role="img" aria-label={alt}>
      <div aria-hidden="true">
        <h3 className="ts-screen-title ts-screen-title--sm">Stats</h3>
        <div className="ts-tiles">
          <div className="ts-tile">
            <strong>3h 20m</strong>
            <span>This week</span>
          </div>
          <div className="ts-tile">
            <strong>4</strong>
            <span>Days read</span>
          </div>
          <div className="ts-tile ts-tile--ember">
            <strong>
              <Flame size={18} /> 12
            </strong>
            <span>Day streak</span>
          </div>
        </div>
        <div className="ts-chart">
          <div className="ts-goalcard-row">
            <span className="ts-caption">Last 12 weeks</span>
            <span className="ts-caption ts-caption--ember">Goal 5h</span>
          </div>
          <svg viewBox={`0 0 ${w} ${h + 4}`} width="100%" aria-hidden="true">
            {WEEKS.map((v, i) => {
              const bh = (v / max) * h;
              return (
                <rect
                  key={i}
                  x={i * (bw + gap)}
                  y={h - bh}
                  width={bw}
                  height={bh}
                  rx={3}
                  fill={v >= goal ? '#4a7c4a' : '#a5d6a7'}
                />
              );
            })}
            <line
              x1="0"
              x2={w}
              y1={goalY}
              y2={goalY}
              stroke="#c46a1b"
              strokeWidth="1.5"
              strokeDasharray="4 4"
            />
          </svg>
        </div>
        <div className="ts-stats-books">
          <span className="ts-caption">Time per book</span>
          <div className="ts-stats-book">
            <span>Emma</span>
            <Bar value={100} thin />
            <span className="ts-caption">11h 40m</span>
          </div>
          <div className="ts-stats-book">
            <span>Walden</span>
            <Bar value={38} thin />
            <span className="ts-caption">4h 25m</span>
          </div>
          <div className="ts-stats-book">
            <span>The Lessons of History</span>
            <Bar value={26} thin />
            <span className="ts-caption">3h 05m</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Widgets({ alt }) {
  const book = BOOKS.emma;
  return (
    <div className="ts-widgets" role="img" aria-label={alt}>
      <div className="ts-widget ts-widget--small" aria-hidden="true">
        <div className="ts-widget-ring">
          <Ring value={0.66} size={72} />
          <span className="ts-widget-ring-label">
            <strong>3h 20m</strong>
            <span>of 5h</span>
          </span>
        </div>
        <span className="ts-streak">
          <Flame /> 12 days
        </span>
      </div>
      <div className="ts-widget ts-widget--medium" aria-hidden="true">
        <div className="ts-widget-ring">
          <Ring value={0.66} size={72} />
          <span className="ts-widget-ring-label">
            <strong>3h 20m</strong>
            <span>of 5h</span>
          </span>
        </div>
        <div className="ts-widget-book">
          <Cover book={book} size="sm" />
          <div>
            <strong>{book.title}</strong>
            <span className="ts-soft">
              p. {book.page} of {book.pages}
            </span>
            <Bar value={60} thin />
          </div>
        </div>
        <span className="ts-streak ts-widget-streak">
          <Flame /> 12 days
        </span>
      </div>
    </div>
  );
}

/* ---------- Files ---------- */

export function FilesApp({ alt }) {
  const rows = [
    ['folder', 'Bookshelf', '5 items'],
    ['folder', 'Currently Reading', '2 items'],
    ['folder', 'Finished Books', '23 items'],
    ['doc', 'Reading Log.txt', '14 KB'],
  ];
  return (
    <div className="ts-files" role="img" aria-label={alt}>
      <div aria-hidden="true">
        <div className="ts-navbar">
          <span className="ts-navbar-back">
            <Icon name="chevron" /> iCloud Drive
          </span>
        </div>
        <h3 className="ts-screen-title ts-screen-title--sm ts-files-title">
          <Icon name="cloud" /> Typesetter
        </h3>
        <ul className="ts-files-list">
          {rows.map(([icon, name, meta]) => (
            <li key={name}>
              <span className={`ts-files-icon ts-files-icon--${icon}`}>
                <Icon name={icon} />
              </span>
              <span className="ts-files-name">{name}</span>
              <span className="ts-caption">{meta}</span>
              <Icon name="chevron" />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function TextFile({ alt }) {
  return (
    <div className="ts-textfile" role="img" aria-label={alt}>
      <div aria-hidden="true">
        <div className="ts-textfile-bar">
          <span className="ts-files-icon ts-files-icon--doc">
            <Icon name="doc" />
          </span>
          <span>Emma.txt</span>
        </div>
        <pre>{`Emma
Jane Austen, 1815
474 pages

Status: Currently Reading
Started: 28 August 2026
Page: 286 of 474
Time read: 11h 40m

Notes
-----
## Volume 1, Chapter 8
Emma talks Harriet out of Robert
Martin, and Knightley is the only
one in the room who sees what she
has done.

- Mr Woodhouse and the gruel
- Every match Emma makes is really
  about Emma`}</pre>
      </div>
    </div>
  );
}
